import { S3BucketEnum } from '@/app/common/app-config/filter-models/files';
import { S3uploaderService } from '@/app/services/s3uploader/s3uploader.service';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { AddDocumentComponent } from '@/app/atoms/add-document/add-document.component';
import { CreatorService } from '@/app/services/creator/creator.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';

@Component({
  selector: 'mc-upload-csv',
  imports: [AddDocumentComponent],
  templateUrl: './upload-csv.html',
  styleUrl: './upload-csv.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadCsv {

  @Input() focussedMonitoringBriefId!: string;
  @Input() focussedScreeningBriefId!: string;
  @Output() close = new EventEmitter<void>();
  @Output() listUploaded = new EventEmitter<boolean>();

  selectedFile: File | null = null;
  uploadingInProgress = signal<boolean>(false);
  savedUploadedFile = signal<{ file: string, fileKey: string, fullUrl: string } | undefined>(undefined);
  loading = signal<boolean>(false);

  /** Services */
  private uploadService = inject(S3uploaderService);
  private creatorService = inject(CreatorService);
  private toastNotificationService = inject(ToastNotificationService);
  /** Services - END */

  onDocumentUploaded(event: { file: string, fileKey: string, fullUrl: string }) {
    console.log('docUploaded: ', event);
    this.savedUploadedFile.set(event);
  }

  getTemplate($event: any) {
    console.log('getTemplate()');
    this.uploadService.secureCreatorCsvDownload();
  }

  submitList($event: any) {
    console.log('submitList() -> Which type? -> ', this.focussedMonitoringBriefId ? 'Monitoring' : 'Screening');
    if (this.focussedMonitoringBriefId) {
      this.submitMonitoringList();
    } else if (this.focussedScreeningBriefId) {
      this.submitScreeningList();
    } else {
      this.toastNotificationService.alert({
        title: 'Whoops! Something went wrong.',
        message: 'Please select a brief to upload the list to.',
        type: 'error',
        icon: 'error'
      });
    }

  }

  submitMonitoringList() {
    console.log('submitMonitoringList()');
    if (!this.focussedMonitoringBriefId) {
      this.toastNotificationService.alert({
        title: 'Whoops! Something went wrong.',
        message: 'Please select a brief to upload the list to.',
        type: 'error',
        icon: 'error'
      });
      return;
    }
    this.loading.set(true);
    this.creatorService.uploadCreatorsCsvMonitoring(this.savedUploadedFile()?.fullUrl ?? '', this.focussedMonitoringBriefId).subscribe((response) => {
      console.log('submit list response: ', response);
      this.loading.set(false);

      const added = response.totalCreated;
      const alreadyPresent = response.totalAlreadyPresent;
      const alreadyPresentText = alreadyPresent > 0 ? ` ${alreadyPresent} were already on this brief.` : '';

      if (response.totalFailed > 0) {
        this.toastNotificationService.alert({
          title: 'Some creators were skipped',
          message: `${response.totalFailed} creator(s) were skipped because they have no email address. Every creator must have an email address. ${added} added.${alreadyPresentText}`,
          type: 'warning',
          icon: 'warning'
        });
      } else {
        this.toastNotificationService.alert({
          title: 'Creators uploaded successfully.',
          message: `${added} creator(s) added to the monitoring brief.${alreadyPresentText}`,
          type: 'success',
          icon: 'success'
        });
      }
      this.listUploaded.emit(true);
    }, (error: unknown) => {
      console.error('error: ', error);
      this.loading.set(false);
      this.toastNotificationService.alert({
        title: 'Whoops! Something went wrong.',
        message: 'Please try again.',
        type: 'error',
        icon: 'error'
      });
    });
  }

  submitScreeningList() {
    console.log('submitScreeningList()');
    if (!this.focussedScreeningBriefId) {
      this.toastNotificationService.alert({
        title: 'Whoops! Something went wrong.',
        message: 'Please select a brief to upload the list to.',
        type: 'error',
        icon: 'error'
      });
      return;
    }
    this.loading.set(true);
    this.creatorService.uploadCreatorsCsvScreening(this.savedUploadedFile()?.fullUrl ?? '', this.focussedScreeningBriefId).subscribe((response) => {
      console.log('submit list response: ', response);
      this.toastNotificationService.alert({
        title: 'Creators uploaded successfully.',
        message: 'The creators have been uploaded to the screening brief.',
        type: 'success',
        icon: 'success'
      });
      this.loading.set(false);
      this.listUploaded.emit(true);
    }, (error: unknown) => {
      console.error('error: ', error);
      this.loading.set(false);
      this.toastNotificationService.alert({
        title: 'Whoops! Something went wrong.',
        message: 'Please try again.',
        type: 'error',
        icon: 'error'
      });
    });
  }

  closeCsv() {
    this.close.emit();
  }

}
